import React, { useState, useEffect } from 'react'
import { ApiService, NewsSpider, NewsSpiderConfig } from '../services/api'
import { SpiderIcon, getSpiderTypeColor, PremiumBadge } from './SpiderIcons'

interface SpiderDashboardProps {
  onSpiderClick: (spider: NewsSpider) => void
  onBulkTrigger: (selectedSpiderIds: string[]) => void
}

const SpiderDashboard: React.FC<SpiderDashboardProps> = ({ onSpiderClick, onBulkTrigger }) => {
  const [spiderConfig, setSpiderConfig] = useState<NewsSpiderConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedSpiders, setSelectedSpiders] = useState<Set<string>>(new Set())
  const [filterType, setFilterType] = useState<string>('all')
  const [sortBy, setSortBy] = useState<'name' | 'type' | 'executions' | 'lastRun'>('name')

  useEffect(() => {
    loadSpiderConfig()
  }, [])

  const loadSpiderConfig = async () => {
    try {
      setLoading(true)
      setError(null)
      const config = await ApiService.getNewsSpidersConfig()
      setSpiderConfig(config)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load spider configuration')
    } finally {
      setLoading(false)
    }
  }

  const handleSpiderToggle = async (spiderId: string, enabled: boolean) => {
    if (!spiderConfig) return

    try {
      const spider = spiderConfig.spiders.find(s => s.id === spiderId)
      if (!spider) return

      await ApiService.updateSpiderConfig(spiderId, {
        enabled,
        maxArticles: spider.maxArticles,
        selectedCategories: spider.selectedCategories || [],
        schedule: spider.schedule
      })

      // Update local state
      setSpiderConfig(prev => ({
        ...prev!,
        spiders: prev!.spiders.map(s => 
          s.id === spiderId ? { ...s, enabled } : s
        ),
        enabledSpiders: prev!.spiders.filter(s => 
          s.id === spiderId ? enabled : s.enabled !== false
        ).length
      }))
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update spider configuration')
    }
  }

  const handleSpiderSelection = (spiderId: string) => {
    const newSelection = new Set(selectedSpiders)
    if (newSelection.has(spiderId)) {
      newSelection.delete(spiderId)
    } else {
      newSelection.add(spiderId)
    }
    setSelectedSpiders(newSelection)
  }

  const handleSelectAll = () => {
    if (!spiderConfig) return
    
    if (selectedSpiders.size === filteredSpiders.length) {
      setSelectedSpiders(new Set())
    } else {
      setSelectedSpiders(new Set(filteredSpiders.map(s => s.id)))
    }
  }

  const formatLastExecution = (lastExecution: string | null) => {
    if (!lastExecution) return 'Never'
    
    const date = new Date(lastExecution)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading spider configuration...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
            <button 
              onClick={loadSpiderConfig}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!spiderConfig) return null

  // Filter and sort spiders
  const filteredSpiders = spiderConfig.spiders
    .filter(spider => filterType === 'all' || spider.type === filterType)
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name)
        case 'type':
          return a.type.localeCompare(b.type)
        case 'executions':
          return b.statistics.totalExecutions - a.statistics.totalExecutions
        case 'lastRun':
          const aTime = a.statistics.lastExecution ? new Date(a.statistics.lastExecution).getTime() : 0
          const bTime = b.statistics.lastExecution ? new Date(b.statistics.lastExecution).getTime() : 0
          return bTime - aTime
        default:
          return 0
      }
    })

  const spiderTypes = ['all', ...spiderConfig.spiderTypes]

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
                </svg>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Total Spiders</dt>
                <dd className="text-lg font-medium text-gray-900">{spiderConfig.totalSpiders}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Active Spiders</dt>
                <dd className="text-lg font-medium text-gray-900">{spiderConfig.enabledSpiders}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-yellow-100 rounded-md flex items-center justify-center">
                <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Premium Spiders</dt>
                <dd className="text-lg font-medium text-gray-900">
                  {spiderConfig.spiders.filter(s => s.premium).length}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-100 rounded-md flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Selected</dt>
                <dd className="text-lg font-medium text-gray-900">{selectedSpiders.size}</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            {/* Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                {spiderTypes.map(type => (
                  <option key={type} value={type}>
                    {type === 'all' ? 'All Types' : type.charAt(0).toUpperCase() + type.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort by</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="name">Name</option>
                <option value="type">Type</option>
                <option value="executions">Total Executions</option>
                <option value="lastRun">Last Run</option>
              </select>
            </div>
          </div>

          <div className="flex space-x-3">
            {/* Select All/None */}
            <button
              onClick={handleSelectAll}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {selectedSpiders.size === filteredSpiders.length ? 'Select None' : 'Select All'}
            </button>

            {/* Bulk Trigger */}
            <button
              onClick={() => onBulkTrigger(Array.from(selectedSpiders))}
              disabled={selectedSpiders.size === 0}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414-1.414L9 10.586V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd"/>
              </svg>
              Trigger Selected ({selectedSpiders.size})
            </button>
          </div>
        </div>
      </div>

      {/* Spider Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredSpiders.map((spider) => (
          <SpiderCard
            key={spider.id}
            spider={spider}
            isSelected={selectedSpiders.has(spider.id)}
            onToggle={handleSpiderToggle}
            onSelect={handleSpiderSelection}
            onClick={() => onSpiderClick(spider)}
          />
        ))}
      </div>

      {filteredSpiders.length === 0 && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47.94-6.071 2.469l-.102.102-.102-.102A7.962 7.962 0 014 15.001C4 11.141 7.141 8 11 8s7 3.141 7 7.001z"/>
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No spiders found</h3>
          <p className="mt-1 text-sm text-gray-500">Try adjusting your filters to see more spiders.</p>
        </div>
      )}
    </div>
  )
}

// Individual spider card component
interface SpiderCardProps {
  spider: NewsSpider
  isSelected: boolean
  onToggle: (spiderId: string, enabled: boolean) => void
  onSelect: (spiderId: string) => void
  onClick: () => void
}

const SpiderCard: React.FC<SpiderCardProps> = ({ 
  spider, 
  isSelected, 
  onToggle, 
  onSelect, 
  onClick 
}) => {
  const formatLastExecution = (lastExecution: string | null) => {
    if (!lastExecution) return 'Never'
    
    const date = new Date(lastExecution)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }

  const isEnabled = spider.enabled !== false

  return (
    <div 
      className={`bg-white rounded-lg shadow-sm border-2 transition-all duration-200 hover:shadow-md cursor-pointer ${
        isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-gray-300'
      } ${!isEnabled ? 'opacity-60' : ''}`}
    >
      {/* Card Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium text-gray-900 truncate">{spider.name}</h3>
            <p className="text-xs text-gray-500 mt-1">{spider.description}</p>
          </div>
          
          {/* Selection checkbox */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation()
                onSelect(spider.id)
              }}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
          </div>
        </div>

        {/* Badges */}
        <div className="mt-3 flex items-center space-x-2">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSpiderTypeColor(spider.type)}`}>
            {spider.type}
          </span>
          {spider.premium && <PremiumBadge />}
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            isEnabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {isEnabled ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>

      {/* Card Body */}
      <div className="p-4 space-y-3" onClick={onClick}>
        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <span className="text-gray-500">Categories:</span>
            <span className="ml-1 font-medium">{spider.categories.length}</span>
          </div>
          <div>
            <span className="text-gray-500">Max Articles:</span>
            <span className="ml-1 font-medium">{spider.maxArticles}</span>
          </div>
          <div>
            <span className="text-gray-500">Total Runs:</span>
            <span className="ml-1 font-medium">{spider.statistics.totalExecutions}</span>
          </div>
          <div>
            <span className="text-gray-500">Success Rate:</span>
            <span className="ml-1 font-medium">
              {spider.statistics.totalExecutions > 0 
                ? `${Math.round((spider.statistics.successfulExecutions / spider.statistics.totalExecutions) * 100)}%`
                : 'N/A'
              }
            </span>
          </div>
        </div>

        {/* Processing Time */}
        <div className="text-xs text-gray-500">
          <span>Processing Time: </span>
          <span className="font-medium">{spider.processingTime}</span>
        </div>

        {/* Last Execution */}
        <div className="text-xs text-gray-500">
          <span>Last Run: </span>
          <span className="font-medium">{formatLastExecution(spider.statistics.lastExecution)}</span>
        </div>

        {/* Features */}
        <div className="flex flex-wrap gap-1">
          {spider.features.slice(0, 2).map((feature, index) => (
            <span 
              key={index}
              className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700"
            >
              {feature}
            </span>
          ))}
          {spider.features.length > 2 && (
            <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700">
              +{spider.features.length - 2} more
            </span>
          )}
        </div>
      </div>

      {/* Card Footer */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 rounded-b-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-500">Status:</span>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={(e) => {
                  e.stopPropagation()
                  onToggle(spider.id, e.target.checked)
                }}
                className="sr-only"
              />
              <div className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${
                isEnabled ? 'bg-blue-600' : 'bg-gray-300'
              }`}>
                <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                  isEnabled ? 'translate-x-3' : 'translate-x-0.5'
                }`} />
              </div>
            </label>
          </div>
          
          <button
            onClick={(e) => {
              e.stopPropagation()
              onClick()
            }}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
          >
            Configure →
          </button>
        </div>
      </div>
    </div>
  )
}

export default SpiderDashboard 